import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { EMPTY, Observable, fromEvent } from 'rxjs';
import { SERVER_VIEWPORT_WIDTH } from 'src/express.tokens';

/**
 * Centralizes access to browser-only APIs so SSR code does not need scattered
 * `typeof window !== 'undefined'` checks. Components and services should use
 * this boundary for `window`, storage, viewport values, and window events.
 *
 * On the server it returns safe null/default values or empty observables; in
 * the browser it forwards to the real native APIs. Server viewport width is
 * request-scoped through SERVER_VIEWPORT_WIDTH so layout decisions still match
 * the production mobile/desktop template without a fake global window.
 */
@Injectable({
    providedIn: 'root',
})
export class BrowserPlatformService {
    private readonly _platformId = inject(PLATFORM_ID);
    private readonly _document = inject<Document>(DOCUMENT);
    private readonly _serverViewportWidth = inject(SERVER_VIEWPORT_WIDTH, { optional: true });

    public readonly isBrowser = isPlatformBrowser(this._platformId);

    public get nativeWindow(): Window | null {
        return this.isBrowser ? window : null;
    }

    public get document(): Document {
        return this._document;
    }

    public get location(): Location | null {
        return this.nativeWindow?.location ?? null;
    }

    public get navigator(): Navigator | null {
        return this.nativeWindow?.navigator ?? null;
    }

    public get userAgent(): string {
        return this.navigator?.userAgent ?? '';
    }

    public get isOnline(): boolean {
        return this.navigator?.onLine ?? true;
    }

    public get viewportWidth(): number {
        return this.nativeWindow?.innerWidth ?? this._serverViewportWidth ?? 0;
    }

    public get viewportHeight(): number {
        return this.nativeWindow?.innerHeight ?? 0;
    }

    public get pageXOffset(): number {
        return this.nativeWindow?.pageXOffset ?? 0;
    }

    public get pageYOffset(): number {
        return this.nativeWindow?.pageYOffset ?? 0;
    }

    public get scrollY(): number {
        return this.nativeWindow?.scrollY ?? 0;
    }

    public get outerHeight(): number {
        return this.nativeWindow?.outerHeight ?? 0;
    }

    public get localStorage(): Storage | null {
        try {
            return this.nativeWindow?.localStorage ?? null;
        } catch {
            return null;
        }
    }

    public get sessionStorage(): Storage | null {
        try {
            return this.nativeWindow?.sessionStorage ?? null;
        } catch {
            return null;
        }
    }

    public fromWindowEvent<T extends Event = Event>(eventName: string): Observable<T> {
        const win = this.nativeWindow;
        return win ? fromEvent<T>(win, eventName) : EMPTY;
    }

    public matchMedia(query: string): boolean {
        const win = this.nativeWindow;
        return !!win?.matchMedia && win.matchMedia(query).matches;
    }

    public scrollTo(options?: ScrollToOptions): void {
        this.nativeWindow?.scrollTo(options);
    }

    public setTimeout(callback: () => void, timeout = 0): number | null {
        return this.nativeWindow?.setTimeout(callback, timeout) ?? null;
    }
}
